import { Injectable, inject } from '@angular/core';
import { lastValueFrom } from 'rxjs';
import { Campaign, CampaignsCampaignAdminApiClient, CampaignsCampaignLocationFilterAdminApiClient, CampaignsSchemeLocationFilterAdminApiClient, SchemeLocationFilter } from 'src/app/api/api-campaigns.service.generated';
import { GUID } from 'src/app/common/utils/GuidUtils';

@Injectable({
  providedIn: 'root'
})
export class CampaignCloneService {
  private campaignsApi = inject(CampaignsCampaignAdminApiClient);
  private campaignLocationFiltersApi = inject(CampaignsCampaignLocationFilterAdminApiClient);
  private schemeLocationFiltersApi = inject(CampaignsSchemeLocationFilterAdminApiClient);

  async cloneCampaign(originalCampaignId: string, advertiserId: string): Promise<{
    campaign: Campaign;
    locationFilters: any;
    schemeLocationFilters?: SchemeLocationFilter[];
  }> {
    const originalCampaign = await lastValueFrom(this.campaignsApi.getCampaign(originalCampaignId));
    const locationFilters = await lastValueFrom(this.campaignLocationFiltersApi.getByCampaignId(originalCampaignId));

    // Fetch scheme location filters only for opener campaigns
    let schemeLocationFiltersArray: SchemeLocationFilter[] | undefined;
    if (originalCampaign.isOpenerStrategy && originalCampaign.strategy?.schemes && Array.isArray(originalCampaign.strategy.schemes)) {
      const schemeLocationFiltersPromises = originalCampaign.strategy.schemes
        .filter(scheme => scheme.id)
        .map(async (scheme) => {
          try {
            const filter = await lastValueFrom(this.schemeLocationFiltersApi.getBySchemeId(scheme.id));
            // Clone the filter and reset schemeId
            return new SchemeLocationFilter({
              ...filter,
              schemeId: undefined
            });
          } catch (error) {
            // If filter doesn't exist, return empty filter
            return new SchemeLocationFilter({ filters: [] });
          }
        });

      schemeLocationFiltersArray = await Promise.all(schemeLocationFiltersPromises);
    }

    const clonedCampaign = new Campaign({
      ...originalCampaign,
      id: undefined,
      campaignId: undefined,
      dateUpdated: undefined,
      title: originalCampaign.title ? `Clone of ${originalCampaign.title}` : undefined,
      advertiserId: advertiserId,
      strategy: this.cloneStrategy(originalCampaign.strategy)
    });

    return {
      campaign: clonedCampaign,
      locationFilters,
      schemeLocationFilters: schemeLocationFiltersArray
    };
  }

  private cloneStrategy(strategy: any): any {
    if (!strategy) return undefined;

    const cloned = JSON.parse(JSON.stringify(strategy));

    if (cloned.id) cloned.id = undefined;

    if (cloned.nextStrategy) {
      cloned.nextStrategy = this.cloneStrategy(cloned.nextStrategy);
    }

    if (cloned.children && Array.isArray(cloned.children)) {
      cloned.children = cloned.children.map(child => this.cloneStrategy(child));
    }

    if (cloned.schemes && Array.isArray(cloned.schemes)) {
      cloned.schemes = cloned.schemes.map(scheme => {
        const clonedScheme = { ...scheme };
        // Generate new ID for cloned scheme (needed for proper saving of location filters)
        clonedScheme.id = GUID.newGuid();
        if (clonedScheme.schemeId) clonedScheme.schemeId = undefined;
        return clonedScheme;
      });
    }

    return cloned;
  }
}
